import moment from "moment";

import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PatientAdvancedSearchControl } from "@/Common/Components/Search/PatientAdvancedSearchControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ClinicalWaitingListsSortOrderType, ClinicalWaitingListsUnclickableColumn, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { IListCreateAndEditItemModel } from "@/Lists/Models/UI/IListCreateAndEditItemModel";
import { ClinicalWaitingListTableItem } from "../Api/Dto/Response/ClinicalWaitingListTableItem";
import { ListHelper } from "../Helpers/ListHelper";
import { IListModel } from "../Models/UI/IListModel";
import { DeleteListAndListItemPopupControl } from "./DeleteListAndListItemPopupControl";
import { EditListNamePopupControl } from "./EditListNamePopupControl";
import { ListPatientInfo } from "./ListPatientInfo";
import { ListsSettingsContainer } from "./ListsSettingsContainer";

import styles from "./ListItems.module.scss";

interface IListItemsProps {
    Model: IListModel;
    CreateAndEditModel: IListCreateAndEditItemModel;
}

class ListItems extends React.Component<IListItemsProps, never> {
    private helper: MyleHelper = new MyleHelper();

    public render(): JSX.Element {
        const saveOptionKey = "ListSettings";
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const cfgm = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        const listName = ListHelper.getNameFormat(this.props.Model.get("ListName"));

        return (
            <div className="workspace" onClick={e => this.stopPropagation(e)}>
                <CommonTableHeaderComponent
                    title={listName}
                    additionalLeftComponent={[(
                        <a
                            key="_editlist"
                            style={{ fontSize: "14px", marginLeft: "5px" }}
                            onClick={() => sessionStore.dispatch(LocalOperations.Lists.EditListName_ShowPopup)}
                        >
                            {cacheStore.GetString("EDITLabel")}
                        </a>
                    )]}
                    searchText={this.props.Model.get("GlobalSearchText")}
                    searchPlaceholder={this.props.Model.get("GlobalSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Lists.SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Lists.OnRefreshButtonClick)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Lists.OnSettingsButtonClick)}
                    onPrintClick={() => this.onPrintClick(columnsOptions)}
                />

                <div id="posref" className="tb ws-scrollable">
                    {this.props.Model.get("ShowDisplaySettings")
                        && <ListsSettingsContainer className="filter-component" Model={this.props.Model} />
                    }
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="List_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Lists.OnRowClick}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_Lists_Search_ColumnSortClick}
                            UnclickableColumnIndex={ClinicalWaitingListsUnclickableColumn.Patient}
                        />
                    </div>
                    <div className="clearfix" />
                </div>

                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-6 col-md-9 col-sm-8 ">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Lists.Search_CurrentPageChange}
                            />
                        </div>
                        <div className="col-lg-6 col-md-3 col-sm-4 text-right">
                            <button className="btn btn-primary" onClick={() => this.addAPatientClick()}>{cacheStore.GetString("AddAPatientLabel")}</button>
                        </div>
                    </div>
                </div>

                {/* EditListNamePopup */}
                {this.props.Model.get("ShowEditListNamePopup") && (
                    <EditListNamePopupControl
                        ContainerId="EditListNameDialog"
                        CancelClickOperation={LocalOperations.Lists.EditListName_Popup_Cancel}
                        OkClickOperation={LocalOperations.Lists.EditListName_Popup_Ok}
                        CurrentListName={this.props.Model.get("ListName")}
                    />
                )}

                {/* DeleteListItemPopup */}
                {this.props.CreateAndEditModel.get("ShowDeleteListItemPopup") && (
                    <DeleteListAndListItemPopupControl
                        ContainerId="EditListNameDialog"
                        CancelClickOperation={LocalOperations.Lists.DeleteListItem_Popup_Cancel}
                        OkClickOperation={LocalOperations.Lists.DeleteListItem_Popup_Ok}
                        Title={cacheStore.GetString("RemovePatientWaitingListLabel")}
                        PatientId={this.props.Model.get("SelectedItem").PatientId}
                        Message={(
                            <>
                                <div>
                                    {cacheStore.GetString("WaitingListPatientDelete1Label")} <b>{NameHelper.formatFullNameForHasPatientName(this.props.Model.get("SelectedItem"), NameHelperFormat.FirstNameFirst)} </b>
                                    {cacheStore.GetString("WaitingListPatientDelete2Label")} <b>{this.props.Model.get("ListName")}</b>?
                                </div>
                                <div>
                                    {cacheStore.GetString("ThisActionCannotBeUndoneLabel")}
                                </div>
                            </>
                        )
                        }
                    />
                )}

                {/* DeleteListPopup */}
                {this.props.Model.get("ShowDeleteListPopup") && (
                    <DeleteListAndListItemPopupControl
                        ContainerId="EditListNameDialog"
                        CancelClickOperation={LocalOperations.Lists.DeleteList_Popup_Cancel}
                        OkClickOperation={LocalOperations.Lists.DeleteList_Popup_Ok}
                        Title={cacheStore.GetString("DeleteWaitingListLabel")}
                        Message={(
                            <div>
                                {cacheStore.GetString("WaitingListDelete1Label")} <b>{this.props.Model.get("ListName")}</b> {cacheStore.GetString("WaitingListDelete2Label")}
                            </div>
                        )}
                    />
                )}

                {/* SearchPatientPopup */}
                {this.props.CreateAndEditModel.get("ShowSearchPatientPopup") && (
                    <Modal
                        showOverlay
                        fixedOverlayPosition
                        showX
                        centerContent
                        width={650}
                        title={cacheStore.GetString("AddPatientToWaitingListMessage")}
                        onClose={() => this.onCancelSearchPatientClick()}
                        footer={(
                            <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-12 columns text-right" />
                            </div>
                        )}
                        draggable
                    >
                        <div className="row">
                            <div className="col-lg-12">
                                <label>{cacheStore.GetString("SearchPatientLabel")}</label>
                                <PatientAdvancedSearchControl
                                    defaultTextValue=""
                                    isFocusOnLoad={true}
                                    componentId="listPatientAdvancedSearch"
                                    maxResultsToReturn={20}
                                    onPatientSelectedOperation={LocalOperations.Lists.SelectPatientToAdd}
                                    isRenderTextbox={true}
                                    isScrollBarActive={true}
                                />
                            </div>
                        </div>
                    </Modal>
                )}

                {/* SelectedPatientPopup */}
                {this.props.CreateAndEditModel.get("ShowSelectedPatientPopup") && (
                    <Modal
                        showOverlay
                        fixedOverlayPosition
                        showX
                        centerContent
                        width={650}
                        title={cacheStore.GetString("AddPatientToWaitingListMessage")}
                        onClose={() => this.onCancelAddPatientClick()}
                        footer={(
                            <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                                    <button
                                        className="btn btn-default btn-sm"
                                        style={{ marginRight: "5px" }}
                                        onClick={() => this.onCancelAddPatientClick()}
                                    >{cacheStore.GetString("CancelLabel")}
                                    </button>
                                    <button
                                        disabled={this.props.CreateAndEditModel.get("IsSelectedPatientExists")}
                                        className="btn btn-primary btn-sm"
                                        onClick={() => this.onOkAddPatientClick()}
                                    >{cacheStore.GetString("OKLabel")}
                                    </button>
                                </div>
                            </div>
                        )}
                        draggable
                    >
                        <div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label>{cacheStore.GetString("SearchPatientLabel")}</label>
                                    <ListPatientInfo Patient={this.props.CreateAndEditModel.get("SelectedPatientForList")} />
                                </div>
                            </div>
                            <div className="row" hidden={this.props.CreateAndEditModel.get("IsSelectedPatientExists")} />
                            <div className="row" style={{ margin: "5px" }}>
                                <span style={{ margin: "5px" }}>{cacheStore.GetString("WaitingListPriorityLabel")}</span>
                                <input
                                    onPaste={e => this.cancelPaste(e)}
                                    onKeyDown={e => this.filterKeyForRefills(e)}
                                    type="number"
                                    min={1}
                                    max={9}
                                    onChange={e => this.onPriorityValueChanged(e)}
                                    value={this.props.CreateAndEditModel.get("Priority")}
                                    className="form-control"
                                    style={{ display: "inline-block", width: "80px", margin: "0 7px" }}
                                    onBlur={e => this.onProrityInputLostFocus(e)}
                                />
                            </div>
                            <div className="row" style={{ margin: "5px" }}>
                                <span style={{ margin: "5px" }}>{cacheStore.GetString("CommentsLabel")}</span>
                                <TextAreaResizable
                                    id="listNewItemCommnents"
                                    text={this.props.CreateAndEditModel.get("Comments")}
                                    isDisabled={false}
                                    onChange={value => this.onCommentsValueChanged(value)}
                                    resetTextOnIdChange={false}
                                    minHeightPx={75}
                                />
                            </div>
                            {this.props.CreateAndEditModel.get("IsSelectedPatientExists") && (
                                <div className="row red-text" style={{ margin: "5px" }}>
                                    <label>{cacheStore.GetString("WaitingListDuplicatePatientEntryMessage")}</label>
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                {/* Edit Item of list Popup */}
                {this.props.CreateAndEditModel.get("OpenEditItemPopup") && (
                    <Modal
                        showOverlay
                        fixedOverlayPosition
                        showX
                        centerContent
                        width={650}
                        title={cacheStore.GetString("PatientInformationLabel")}
                        onClose={() => this.onCancelEditItemClick()}
                        footer={(
                            <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                                    <button
                                        className="btn btn-default btn-sm"
                                        style={{ marginRight: "5px" }}
                                        onClick={() => this.onCancelEditItemClick()}
                                    >
                                        {cacheStore.GetString("CancelLabel")}
                                    </button>
                                    <button
                                        hidden={!this.props.CreateAndEditModel.get("IsSelectedPatientExists")}
                                        className="btn btn-primary btn-sm"
                                        onClick={() => this.onOkEditItemClick()}
                                    >
                                        {cacheStore.GetString("OKLabel")}
                                    </button>
                                </div>
                            </div>
                        )}
                        draggable
                    >
                        <div>
                            <div className={`row ${styles.priorityDiv}`}>
                                <div className="col-sm-2 col-md-2 col-lg-2 vcenter text-right">
                                    <label>{cacheStore.GetString("WaitingListPriorityLabel")}</label>
                                </div>
                                <div className="col-sm-10 col-md-10 col-lg-10">
                                    <input
                                        onPaste={e => this.cancelPaste(e)}
                                        onKeyDown={e => this.filterKeyForRefills(e)}
                                        type="number"
                                        min={1}
                                        max={9}
                                        onChange={e => this.onPriorityValueChanged(e)}
                                        value={this.props.CreateAndEditModel.get("Priority")}
                                        className="form-control"
                                        style={{ display: "inline-block", width: "80px" }}
                                        onBlur={e => this.onProrityInputLostFocus(e)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-2 col-md-2 col-lg-2 vcenter text-right">
                                    <label>{cacheStore.GetString("CommentsLabel")}</label>
                                </div>
                                <div className="col-sm-10 col-md-10 col-lg-10">
                                    <TextAreaResizable
                                        id="listcommnents"
                                        text={this.props.CreateAndEditModel.get("Comments")}
                                        isDisabled={false}
                                        onChange={value => this.onCommentsValueChanged(value)}
                                        minHeightPx={75}
                                        resetTextOnIdChange={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}

                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Lists.SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    private onProrityInputLostFocus(e: React.FocusEvent<HTMLInputElement>): void {
        let operation = new LocalEvent(LocalOperations.Lists.EditItem_ValidatePriorityValue);
        operation.AddItem("priority", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private filterKeyForRefills(e: React.KeyboardEvent): void {
        if (e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 69)
            e.preventDefault();
    }

    private cancelPaste(e: React.ClipboardEvent<HTMLInputElement>): void {
        e.preventDefault();
    }

    private stopPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private onCancelEditItemClick(): void {
        let operation = new LocalEvent(LocalOperations.Lists.EditItem_Popup_Cancel);
        LocalEventDispatcher.dispatch(operation);
    }

    private onOkEditItemClick(): void {
        let operation = new LocalEvent(LocalOperations.Lists.EditItem_Popup_Ok);
        operation.AddItem("listId", this.props.Model.get("ListId"));
        LocalEventDispatcher.dispatch(operation);
    }

    private onPriorityValueChanged(e: React.ChangeEvent<HTMLInputElement>): void {
        let operation = new LocalEvent(LocalOperations.Lists.EditItem_PriorityValueChanged);
        operation.AddItem("priority", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private onCommentsValueChanged(value: string): void {
        let operation = new LocalEvent(LocalOperations.Lists.EditItem_CommentsValueChanged);
        operation.AddItem("comments", value);
        LocalEventDispatcher.dispatch(operation);
    }

    private onCancelAddPatientClick(): void {
        let operation = new LocalEvent(LocalOperations.Lists.CloseSelectedPatientPopup);
        LocalEventDispatcher.dispatch(operation);
    }

    private onOkAddPatientClick(): void {
        let operation = new LocalEvent(LocalOperations.Lists.SavePatientForList);
        operation.AddItem("listId", this.props.Model.get("ListId"));
        LocalEventDispatcher.dispatch(operation);
    }

    private addAPatientClick(): void {
        let operation = new LocalEvent(LocalOperations.Lists.OnAddAPatientClick);
        LocalEventDispatcher.dispatch(operation);
    }

    private onRemoveItemClick(patientId: string): void {
        let operation = new LocalEvent(LocalOperations.Lists.DeleteListItem_ShowPopup);
        operation.AddItem("value", patientId);
        LocalEventDispatcher.dispatch(operation);
    }

    private onCancelSearchPatientClick(): void {
        sessionStore.dispatch(LocalOperations.Lists.CloseSearchPatientPopup);
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    // table

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<ClinicalWaitingListTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Patient":
                        return {
                            renderCell: item => (
                                <HyperLinkRedirectControl
                                    target={RedirectTo.Patient}
                                    redirectValue={item.Model.PatientContactId}
                                    text={item.Model.PatientNameAndIdentifiers}
                                />
                            )
                        };

                    case "Priority":
                        return {
                            renderCell: item => (
                                <div>{item.Model.Priority}</div>
                            )
                        };

                    case "Phone":
                        return {
                            renderCell: item => (
                                <>
                                    {item.Model.PatientPhoneNumbers.map((p, idx) => (
                                        <div key={"phn_" + idx}>{p.format()}</div>
                                    ))}
                                </>
                            )
                        };

                    case "Comments":
                        return {
                            renderCell: item => (
                                <div>{item.Model.Comments}</div>
                            )
                        };

                    case "DateAdded":
                        return {
                            renderCell: item => {
                                let date = moment(item.Model.DateAdded).format("YYYY/MM/DD");
                                return (<div>{date}</div>);
                            }
                        };

                    case "Remove":
                        return {
                            renderCell: item => (
                                <div>
                                    <a onClick={() => this.onRemoveItemClick(item.Model.PatientId)}>{cacheStore.GetString("WaitingListRemoveLabel")}</a>
                                </div>
                            )
                        };
                }
            },
            false
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "Patient",
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                // IsSortActive: true,
                IsSortable: true,
                SortDirection: SortDirection.ASC,
                SortColumn: ClinicalWaitingListsSortOrderType.Patient
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("WaitingListPriorityLabel"),
                FieldName: "Priority",
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                // IsSortActive: true,
                IsSortable: true,
                // SortDirection: SortDirection.ASC,
                SortColumn: ClinicalWaitingListsSortOrderType.Priority
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PhoneLabel"),
                FieldName: "Phone",
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CommentsLabel"),
                FieldName: "Comments",
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                // IsSortActive: true,
                IsSortable: true,
                // SortDirection: SortDirection.ASC,
                SortColumn: ClinicalWaitingListsSortOrderType.Comments
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("WaitingListDateAddedLabel"),
                FieldName: "DateAdded",
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                // IsSortActive: true,
                IsSortable: true,
                // SortDirection: SortDirection.ASC,
                SortColumn: ClinicalWaitingListsSortOrderType.DateAdded
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "Remove",
                WidthPixels: 200,
                IsFixedInPlace: true
            })
        ];
    }

    private onPrintClick(currentColumnsState: Immutable.List<MyleTableColumnOptions>): void {
        let columnArray: MyleTableColumnOptions[] = currentColumnsState.toArray().filter(co => !co.IsFixedInPlace && !co.IsHidden);
        let columnsInfo = this.helper.castTableColumnOptionsToTableColumnInfo(columnArray);

        let validation = new ValidationHelper();
        let phoneValidationRule = validation.getPhoneNumberValidationRule(cacheStore.GetDefaultCountryForActiveSite().LookupID);

        let event = new LocalEvent(LocalOperations.Lists.OnPrintButtonClick);
        event.AddItem("columnsInfo", columnsInfo);
        event.AddItem("phoneValidationRuleId", phoneValidationRule.Id);
        LocalEventDispatcher.dispatch(event);
    }
}

export { ListItems };
